import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  History,
  FileSpreadsheet,
  User,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  Share2,
} from "lucide-react";
import { useAuth } from "../App";
import { ShareUploadDialog } from "../components/ShareUploadDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UploadHistory = () => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [uploadDetail, setUploadDetail] = useState(null);
  const [shareUpload, setShareUpload] = useState(null);
  const { user } = useAuth();
  const canShare = user?.role !== "Viewer";

  const fetchUploads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/uploads?page=${page}&limit=20`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUploads(data.uploads);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (error) {
      console.error("Failed to fetch uploads:", error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUploads();
  }, [fetchUploads]);

  const viewUploadDetail = async (upload) => {
    setSelectedUpload(upload);
    setDetailModalOpen(true);
    
    try {
      const response = await fetch(`${API}/uploads/${upload.upload_id}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUploadDetail(data);
      }
    } catch (error) {
      console.error("Failed to fetch upload detail:", error);
    }
  };

  return (
    <Layout title="Upload History" subtitle="View all your lead uploads">
      <ShareUploadDialog upload={shareUpload} open={!!shareUpload} onOpenChange={(o) => !o && setShareUpload(null)} />
      <Card className="border-border/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <History className="w-5 h-5" />
            Upload History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : uploads.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No uploads yet</p>
              <p className="text-sm">Start by uploading your first CSV file</p>
            </div>
          ) : (
            <div className="space-y-4">
              {uploads.map((upload) => (
                <div
                  key={upload.upload_id}
                  className="border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                        <FileSpreadsheet className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{upload.file_name}</h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {upload.user_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(upload.created_at).toLocaleDateString()}{" "}
                            {new Date(upload.created_at).toLocaleTimeString()}
                          </span>
                          {upload.uploaded_at && (
                            <span className="text-primary" data-testid={`history-upload-date-${upload.upload_id}`}>
                              Upload date: {new Date(`${upload.uploaded_at}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}{upload.date_from_filename ? " (from file name)" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-lg font-bold tabular-nums">
                            {upload.total_records.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-green-500">
                            {upload.unique_records.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">Unique</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-amber-500">
                            {upload.internal_duplicates.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">Internal</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-red-500">
                            {upload.historical_duplicates.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">Historical</p>
                        </div>
                      </div>

                      {canShare && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShareUpload(upload)}
                        data-testid={`share-upload-${upload.upload_id}`}
                      >
                        <Share2 className="w-4 h-4 mr-1" />
                        Download / Email
                      </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewUploadDetail(upload)}
                        data-testid={`view-upload-${upload.upload_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="history-prev-page"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  data-testid="history-next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Details</DialogTitle>
          </DialogHeader>
          {selectedUpload && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">File Name</p>
                  <p className="font-medium">{selectedUpload.file_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Uploaded By</p>
                  <p className="font-medium">{selectedUpload.user_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Date</p>
                  <p className="font-medium">
                    {new Date(selectedUpload.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                  <Badge variant="outline" className="bg-green-500/10 text-green-500">
                    {selectedUpload.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <Card className="border-border/60">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold tabular-nums">
                      {selectedUpload.total_records.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Total Records
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border/60">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold tabular-nums text-green-500">
                      {selectedUpload.unique_records.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Unique</p>
                  </CardContent>
                </Card>
                <Card className="border-border/60">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold tabular-nums text-amber-500">
                      {selectedUpload.internal_duplicates.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Internal Dups
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border/60">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold tabular-nums text-red-500">
                      {selectedUpload.historical_duplicates.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Historical Dups
                    </p>
                  </CardContent>
                </Card>
              </div>

              {uploadDetail?.sample_leads?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Sample Records</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full data-table text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">Name</th>
                          <th className="text-left py-2 px-2">Phone</th>
                          <th className="text-left py-2 px-2">Email</th>
                          <th className="text-left py-2 px-2">City</th>
                          <th className="text-left py-2 px-2">State</th>
                          <th className="text-center py-2 px-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadDetail.sample_leads.slice(0, 10).map((lead) => (
                          <tr key={lead.lead_id} className="border-b">
                            <td className="py-2 px-2">
                              {lead.first_name} {lead.last_name}
                            </td>
                            <td className="py-2 px-2 tabular-nums">{lead.phone}</td>
                            <td className="py-2 px-2 truncate max-w-[150px]">{lead.email}</td>
                            <td className="py-2 px-2">{lead.city}</td>
                            <td className="py-2 px-2">{lead.state}</td>
                            <td className="py-2 px-2 text-center">
                              {lead.is_duplicate ? (
                                <Badge
                                  variant="outline"
                                  className={
                                    lead.duplicate_type === "internal"
                                      ? "bg-amber-500/10 text-amber-500"
                                      : "bg-red-500/10 text-red-500"
                                  }
                                >
                                  {lead.duplicate_type}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-green-500/10 text-green-500">
                                  unique
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default UploadHistory;
